/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, FileText, Loader2, Type, File, Copy, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { Document } from "@/lib/interfaces";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "./ui/command";
import apiService from "@/services/apiService";
import { fileToBase64 } from "@/lib/helper";
import useErrorStore from "@/store/confirmationStore";

interface FileUploadProps {
  onFileProcessed: (data: any) => void;
  docType?: string;
}

// Document types
const documentTypes = [
  { value: "107", label: "CrPC 107/BNSS 126 - किसी बात के करने का अभिप्रेरण" },
  // { value: "111", label: "CrPC 111/BNSS 130 - जब अभिप्रेरित एक कृत्य के स्थान पर भिन्न कृत्य किया जाता है, तब अभिप्रेरक का दायित्व" },
  // { value: "116_3", label: "CrPC 116(3)/BNSS 135(3) - कारावास से दंडनीय अपराध के अभिप्रेरण का दंड - यदि वह अपराध न किया गया हो (उपधारा 3: आदेश के पालन के लिए बंधपत्र की माँग करने का मजिस्ट्रेट का अधिकार)" },
  // { value: "117", label: "CrPC 117/BNSS 136 - जनसमुदाय या दस या अधिक व्यक्तियों द्वारा अपराध के करने का अभिप्रेरण" },
  // { value: "other", label: "Other Document" },
];

async function extractTextFromFile(file: File, documentType: string): Promise<string> {
  const formData = new FormData();
  formData.append("pdf", file);
  try {
    const response: any = await apiService.post(
      `/extract-text?documentType=${documentType}`,
      formData,
      { headers: { "Content-Type": undefined }, timeout: 0 }
    );
    if (!response) {
      throw new Error(`Server responded with status ${response.status}`);
    }
    return response.message || {};
  } catch (error) {
    console.error("OCR request failed:", error);
    throw error;
  }
}

async function processWithOpenAI(extractedText: string, documentType: string): Promise<any> {
  console.log(documentType);
  const formData = {
    text: extractedText,
  };
  try {
    const data: any = await apiService.post(`extract-text/text?documentType=${documentType}`, JSON.stringify(formData));
    if (!data) {
      throw new Error(`Server responded with status ${data.status}`);
    }
    return data.message || {};
  } catch (error) {
    console.error("OCR request failed:", error);
    throw error;
  }
}

export function FileUpload({ onFileProcessed, docType }: FileUploadProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState("");
  const [documentType, setDocumentType] = useState<string>(docType || "107");
  const [activeTab, setActiveTab] = useState("file");
  const [progressStage, setProgressStage] = useState(0);
  const [hasFinalResponse, setHasFinalResponse] = useState(false);
  const [isProcessingText, setIsProcessingText] = useState(false);
  const [parentDocumentId, setParentDocumentId] = useState("");
  const [selectedDocName, setSelectedDocName] = useState("");
  const [isOpen, setIsOpen] = useState(true);
  const [availableParentDocuments, setAvailableParentDocuments] = useState<Document[]>([]);
  const showError = useErrorStore((s) => s.showError);

  const progressStages = ["Starting extraction for document", "Uploaded to gs pabandiportal", "Extracted text from uploaded document", "Got chat response for document", "Deleted from gs pabandiportal document after processing", "Extraction and transformation complete for document"];

  useEffect(() => {
    fetchAvailableDocuments();
  }, [documentType]);

  const fetchAvailableDocuments = async (search: string = "") => {
    if (documentType && documentType !== "107") {
      const jsonResponse: any = await apiService.get(`documents?limit=${10}&page=${1}&search=${search}`);
      setAvailableParentDocuments(jsonResponse.data);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file || !documentType) {
        showError("Document type required. Please select a document type before uploading files.");
        return;
      }

      const base64 = await fileToBase64(file);
      sessionStorage.setItem("uploaded_pdf_base64", base64);

      setUploadedFile(file);
      setIsProcessing(true);
      setProgressStage(0);
      setHasFinalResponse(false);

      let stage = 0;
      const interval = setInterval(() => {
        stage += 1;
        if (stage < progressStages.length) {
          setProgressStage(stage);
        } else {
          clearInterval(interval);
        }
      }, 3000);

      try {
        // Step 1: Perform OCR
        const structuredData: any = await extractTextFromFile(file, documentType);
        setProgressStage(progressStages.length);
        setHasFinalResponse(true);

        if (parentDocumentId && structuredData) {
          structuredData["parentDocumentId"] = parentDocumentId;
        }
        structuredData["documentType"] = documentType;
        // Step 2: Pass data to parent component
        onFileProcessed(structuredData);

        toast("Document processed successfully", {
          description: "Your document has been processed and data extracted.",
        });
      } catch (error) {
        console.error("Error processing file:", error);
        showError("Error occurred while processing document, please retry.");
      } finally {
        setIsProcessing(false);
        clearInterval(interval);
      }
    },
    [onFileProcessed, documentType, parentDocumentId]
  );

  const handleTextProcess = async () => {
    if (!pastedText.trim() || !documentType) {
      showError("Missing information. Please select a document type and enter text to process.");
      return;
    }

    setIsProcessingText(true);

    try {
      // Process pasted text with OpenAI
      console.log("Processing pasted text with OpenAI...");
      const structuredData = await processWithOpenAI(pastedText, documentType);
      structuredData.file_name = `pasted_text_${Date.now()}.txt`;

      if (parentDocumentId && structuredData) {
        structuredData["parentDocumentId"] = parentDocumentId;
        structuredData["documentType"] = documentType;
      }
      // Pass data to parent component
      onFileProcessed(structuredData);

      toast("Text processed successfully", {
        description: "Your pasted text has been processed and structured.",
      });

      setPastedText("");
    } catch (error) {
      console.error("Error processing text:", error);
      showError("Processing failed. Failed to process the pasted text. Please try again.");
    } finally {
      setIsProcessingText(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false,
  });

  const canProcess = documentType && ((activeTab === "file" && uploadedFile) || (activeTab === "text" && pastedText.trim()));

  let timeoutId: any;
  const handleSearch = (value: string) => {
    if (!value) {
      fetchAvailableDocuments();
      return;
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fetchAvailableDocuments(value);
      clearTimeout(timeoutId);
    }, 600);
  };

  return (
    <div className="w-full space-y-4">
      {/* Document Type Selection */}
      {docType != '3_4' && 
      <Card className="border border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-black">Document Type</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={documentType} onValueChange={setDocumentType}>
            <SelectTrigger className="w-full border-gray-300">
              <SelectValue placeholder="Select document type..." />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-200">
              {documentTypes.map((type) => (
                <SelectItem key={type.value} value={type.value} className="hover:bg-gray-50">
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!documentType && <p className="text-sm text-red-600 mt-2">Please select a document type before uploading</p>}
          {/* Conditional: If document type is NOT 107, show parent document select */}
          {documentType && documentType !== "107" && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Document</label>

              <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" aria-expanded={isOpen} className="w-full justify-between border-gray-300">
                    {selectedDocName || "Search & select parent document..."}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>

                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput placeholder="Search documents..." onValueChange={handleSearch} />
                    <CommandEmpty>No document found.</CommandEmpty>
                    <CommandGroup>
                      {availableParentDocuments.map((doc) => (
                        <CommandItem
                          key={doc.id}
                          value={doc.fileName || doc.originalName}
                          onSelect={() => {
                            setParentDocumentId(doc.id);
                            setSelectedDocName(doc.fileName || doc.originalName);
                            setIsOpen(false);
                          }}
                        >
                          {doc.fileName || doc.originalName} - ({doc.generalDiary?.gdNo || "N/A"}) - {doc.generalDiary?.suspects?.map((s) => s.name + " S/O " + s.fatherName).join(", ") || "N/A"}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </CardContent>
      </Card>
      }

      {/* Upload Options */}
      <Card className="border border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-black">Upload Document</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-gray-100">
              <TabsTrigger value="file" className="flex items-center space-x-2">
                <File className="h-4 w-4" />
                <span>Choose File</span>
              </TabsTrigger>
              <TabsTrigger value="text" className="flex items-center space-x-2">
                <Type className="h-4 w-4" />
                <span>Paste Text</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="file" className="mt-4">
              <div {...getRootProps()} className={cn("border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors", isDragActive ? "border-black bg-gray-50" : "border-gray-300 hover:border-gray-400 hover:bg-gray-50", isProcessing && "pointer-events-none opacity-50", !documentType && "pointer-events-none opacity-50")}>
                <input {...getInputProps()} />

                <div className="flex flex-col items-center space-y-4">
                  {isProcessing ? <Loader2 className="h-12 w-12 text-gray-400 animate-spin" /> : <Upload className="h-12 w-12 text-gray-400" />}

                  <div className="space-y-2">
                    <p className="text-lg font-medium text-black">{isProcessing ? "Processing document..." : isDragActive ? "Drop your documents here" : "Drop your documents here or click to browse"}</p>
                    <p className="text-sm text-gray-500">Supports PDF files up to 10MB</p>
                  </div>

                  {!isProcessing && documentType && <Button className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">Choose Files</Button>}
                </div>
              </div>

              {uploadedFile && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3 mb-3">
                    <FileText className="h-5 w-5 text-gray-500" />
                    <span className="text-sm text-gray-700">{uploadedFile.name}</span>
                    {isProcessing && <Loader2 className="h-4 w-4 text-gray-500 animate-spin ml-auto" />}
                  </div>

                  {isProcessing && (
                    <div className="w-full">
                      {/* Progress bar */}
                      <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden mb-2">
                        <div className="h-full bg-black transition-all duration-700 ease-in-out" style={{ width: `${(progressStage / progressStages.length) * 100}%` }} />
                      </div>
                      {/* Steps */}
                      <ul className="space-y-1 text-sm text-gray-700">
                        {progressStages.map((stage, index) => (
                          <li key={index} className={cn(index < progressStage ? "text-black font-medium" : index === progressStage && hasFinalResponse ? "text-black font-medium" : "text-gray-400")}>
                            {index < progressStage || (index === progressStage && hasFinalResponse) ? "✓" : "•"} {stage}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="text" className="mt-4">
              <div className="space-y-4">
                <Textarea placeholder="Paste your document text here..." value={pastedText} onChange={(e) => setPastedText(e.target.value)} className="min-h-[200px] border-gray-300 resize-none" disabled={!documentType || isProcessingText} />

                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">
                    {pastedText.length} characters, {pastedText.split(/\s+/).filter((word) => word.length > 0).length} words
                  </p>

                  <Button onClick={handleTextProcess} disabled={!canProcess || isProcessingText} className="bg-black text-white hover:bg-gray-800 disabled:opacity-50">
                    {isProcessingText ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" />
                        Process Text
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Processing Status */}
      {(isProcessing || isProcessingText) && (
        <Card className="border border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
              <div>
                <p className="text-sm font-medium text-blue-900">Processing Document</p>
                <p className="text-xs text-blue-700">{activeTab === "file" ? "Extracting text and analyzing content..." : "Analyzing pasted text..."}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
